import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch } from 'react-icons/fi';
import { recitalService } from '../../../services/RecitalService';
import { resolveColor, lookStyles } from '../colorLook';

// THE class library: one list of the recital's classes, on the left, in both
// views. It replaces the Assign Board's tray and the right panel's "Class
// library" tab, which had drifted into two different lists of the same thing.
// Andrew, 2026-10-12 (mockup v6): "Library on the left makes sense"; "there is
// like multiple class library sections".
//
// Each row carries one pip per show: filled when the class is in that show,
// click to put it in or take it out. That replaces the old "Show 1 / Show 2"
// chips and the "Adding to" picker. Rows are also draggable onto a board column.
// Routines (classes added in the recital, not copied from the schedule) sit in
// their own "New routines" group at the bottom.

const DAY_ORDER = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABEL = { monday:'Monday', tuesday:'Tuesday', wednesday:'Wednesday',
                    thursday:'Thursday', friday:'Friday', saturday:'Saturday', sunday:'Sunday' };
const DAY_ABBR  = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

const S = {
    wrap:   { width:270, flexShrink:0, background:'#fff', borderRight:'1px solid #e0e0ec', display:'flex', flexDirection:'column', minHeight:0 },
    head:   { padding:'12px 14px 8px', borderBottom:'1px solid #f0f0f8' },
    title:  { fontSize:13, fontWeight:700, color:'#3f3f55' },
    sub:    { fontSize:11, color:'#9999bb', marginTop:2, lineHeight:1.4 },
    tools:  { padding:'8px 12px 0', display:'flex', flexDirection:'column', gap:6 },
    searchWrap: { position:'relative' },
    searchIcon: { position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9b9bb8' },
    search: { width:'100%', background:'#f2f3f9', border:'1px solid #d9d9e8', borderRadius:7, padding:'7px 10px 7px 28px',
              fontSize:12, color:'#3f3f55', outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
    row:    { display:'flex', gap:5 },
    sel:    { flex:1, fontSize:11, border:'1px solid #d9d9e8', borderRadius:6, padding:'4px 6px', background:'#fff',
              color:'#5a5a7a', fontFamily:'inherit', minWidth:0, outline:'none' },
    importBtn: { margin:'8px 12px 0', fontSize:12, fontWeight:600, color:'#29b8e5', border:'1px dashed #a8dff2', borderRadius:7,
                 padding:7, textAlign:'center', background:'#f4fbfe', cursor:'pointer', fontFamily:'inherit' },
    importNote: { margin:'4px 14px 0', fontSize:10, color:'#b0b0cc', lineHeight:1.4 },
    warn:   { margin:'8px 12px 0', background:'#fffbf0', border:'1px solid #f0c87a', borderRadius:8, padding:'7px 10px',
              fontSize:11, color:'#b06d08', lineHeight:1.4, fontWeight:500 },
    scroll: { flex:1, overflowY:'auto', padding:'6px 12px 12px', minHeight:0 },
    grp:    { fontSize:10, fontWeight:700, color:'#9999bb', textTransform:'uppercase', letterSpacing:0.5, margin:'10px 2px 6px', display:'flex' },
    grpN:   { marginLeft:'auto', fontWeight:500 },
    cls:    (placed, dragging, look) => ({
        border:'1px solid #e8e8f2', borderRadius:8, padding:'8px 10px', marginBottom:6, background:'#fff',
        display:'flex', alignItems:'center', gap:9, cursor:'grab', userSelect:'none',
        opacity: dragging ? 0.35 : placed ? 0.7 : 1,
        ...look,
    }),
    name:   { fontSize:13, fontWeight:600, color:'#3f3f55', lineHeight:1.2, display:'flex', alignItems:'center' },
    meta:   { fontSize:11, color:'#8a8aa8', marginTop:2, lineHeight:1.3 },
    pips:   { marginLeft:'auto', display:'flex', gap:3, flexShrink:0 },
    pip:    (on) => ({ width:18, height:18, borderRadius:5, fontSize:10, fontWeight:700, display:'flex', alignItems:'center',
              justifyContent:'center', cursor:'pointer', border:`1px solid ${on ? '#29b8e5' : '#e0e0ec'}`,
              color: on ? '#fff' : '#c0c0d8', background: on ? '#29b8e5' : '#fff', fontFamily:'inherit', padding:0 }),
    tag:    { fontSize:9, fontWeight:700, color:'#8b35c8', background:'#f5edff', border:'1px solid #c8a0f0', borderRadius:3,
              padding:'1px 4px', marginLeft:5 },
    dot:    (c) => ({ display:'inline-block', width:9, height:9, borderRadius:'50%', background:c, marginRight:6, flexShrink:0 }),
    empty:  { fontSize:12, color:'#9b9bb8', fontStyle:'italic', padding:'12px 4px', lineHeight:1.5 },
};

export const isRoutine = (cls) => cls && cls.sourceStickerId == null;

export default function ClassLibrary({ recital, shows, colorBy = 'style', colorLook = 'stripe', colorMap = {}, colorToLevelName = {},
    onToggleInShow, onRefresh, onDragStart, onDragEnd, draggingClassId = null }) {
    const classes = useMemo(() => recital?.classes || [], [recital]);
    const [search, setSearch]   = useState('');
    const [fStyle, setFStyle]   = useState('');
    const [fTeacher, setFTeacher] = useState('');
    const [fLevel, setFLevel]   = useState('');
    const [groupBy, setGroupBy] = useState('day');
    const [importing, setImporting] = useState(false);
    const [busyKey, setBusyKey] = useState(null); // "classId:showIdx" while a pip saves

    // classId -> show indices it appears in
    const placement = useMemo(() => {
        const map = {};
        shows.forEach((show, idx) => (show.acts || []).forEach((act) => {
            if (act.recitalClassId) (map[act.recitalClassId] = map[act.recitalClassId] || []).push(idx);
        }));
        return map;
    }, [shows]);

    const styleOpts   = useMemo(() => [...new Set(classes.flatMap((c) => c.styles || []))].sort(), [classes]);
    const teacherOpts = useMemo(() => [...new Set(classes.flatMap((c) => c.teachers || []))].sort(), [classes]);
    const levelOpts   = useMemo(() => [...new Set(classes.flatMap((c) => (c.levels || []).map((l) => colorToLevelName?.[l]).filter(Boolean)))].sort(),
        [classes, colorToLevelName]);

    const q = search.trim().toLowerCase();
    const filtered = classes.filter((c) => {
        if (q && !(c.className.toLowerCase().includes(q)
            || (c.styles || []).some((s) => s.toLowerCase().includes(q))
            || (c.teachers || []).some((t) => t.toLowerCase().includes(q)))) return false;
        if (fStyle && !(c.styles || []).includes(fStyle)) return false;
        if (fTeacher && !(c.teachers || []).includes(fTeacher)) return false;
        if (fLevel && !(c.levels || []).some((l) => colorToLevelName?.[l] === fLevel)) return false;
        return true;
    });

    const groupKey = (c) => {
        if (groupBy === 'placed')  return placement[c.id]?.length ? 'Placed' : 'Not placed';
        if (groupBy === 'day')     return c.dayOfTheWeek ? (DAY_LABEL[c.dayOfTheWeek] || c.dayOfTheWeek) : 'Other';
        if (groupBy === 'level')   return (c.levels?.[0] && colorToLevelName?.[c.levels[0]]) || 'No level';
        if (groupBy === 'style')   return c.styles?.[0] || 'No style';
        if (groupBy === 'teacher') return c.teachers?.[0] || 'No teacher';
        return 'All';
    };
    const grouped = {};
    const routines = [];
    filtered.forEach((c) => {
        if (isRoutine(c)) { routines.push(c); return; }
        const k = groupKey(c);
        (grouped[k] = grouped[k] || []).push(c);
    });
    let keys = Object.keys(grouped);
    if (groupBy === 'day') {
        keys.sort((a, b) => {
            const ai = DAY_ORDER.indexOf(a.toLowerCase()), bi = DAY_ORDER.indexOf(b.toLowerCase());
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
    } else if (groupBy === 'placed') {
        keys.sort((a) => (a === 'Not placed' ? -1 : 1));
    } else keys.sort();

    const unplacedTotal = classes.filter((c) => !(placement[c.id]?.length)).length;

    const importFromSchedule = async () => {
        setImporting(true);
        try {
            const res = await recitalService.importClasses(recital.id, { importAll: true });
            const added = res.data?.classes?.length ?? 0;
            toast.success(added > 0
                ? `Imported ${added} ${added === 1 ? 'class' : 'classes'} from your schedule.`
                : 'Everything on your schedule is already in this recital.');
            await onRefresh();
        } catch (e) {
            console.error(e);
            toast.error('Could not import classes. Please try again.');
        } finally { setImporting(false); }
    };

    const togglePip = async (cls, idx) => {
        if (!onToggleInShow || busyKey) return;
        setBusyKey(`${cls.id}:${idx}`);
        try { await onToggleInShow(cls, idx); }
        finally { setBusyKey(null); }
    };

    const meta = (c) => {
        if (isRoutine(c)) return `${(c.students || []).length} dancer${(c.students || []).length === 1 ? '' : 's'} · not on the schedule`;
        const parts = [...(c.styles || []), ...(c.teachers || []), ...(c.levels || []).map((l) => colorToLevelName?.[l]).filter(Boolean)];
        if (groupBy !== 'day' && c.dayOfTheWeek) parts.push(DAY_ABBR[c.dayOfTheWeek] || c.dayOfTheWeek);
        parts.push(`${(c.students || []).length} dancer${(c.students || []).length === 1 ? '' : 's'}`);
        return parts.join(' · ');
    };

    const renderRow = (c) => {
        const color  = resolveColor(c, colorBy, colorMap);
        const look   = lookStyles(color, colorLook);
        const placed = !!placement[c.id]?.length;
        return (
            <div key={c.id}
                draggable={!!onDragStart}
                onDragStart={() => onDragStart && onDragStart(c)}
                onDragEnd={() => onDragEnd && onDragEnd()}
                style={S.cls(placed, draggingClassId === c.id, look.card)}
                title={onDragStart ? 'Drag onto a show, or click a number' : 'Click a number to put it in that show'}>
                <div style={{ minWidth:0, flex:1 }}>
                    <div style={S.name}>
                        {look.marker && <span style={S.dot(look.marker)}/>}
                        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.className}</span>
                        {isRoutine(c) && <span style={S.tag}>ROUTINE</span>}
                    </div>
                    <div style={S.meta}>{meta(c)}</div>
                </div>
                <div style={S.pips}>
                    {shows.map((show, idx) => {
                        const on = (placement[c.id] || []).includes(idx);
                        const busy = busyKey === `${c.id}:${idx}`;
                        return (
                            <button key={show.id} type="button" style={{ ...S.pip(on), opacity: busy ? 0.5 : 1 }}
                                aria-label={`${on ? 'Remove from' : 'Add to'} ${show.name || `Show ${idx + 1}`}`}
                                title={`${on ? 'Remove from' : 'Add to'} ${show.name || `Show ${idx + 1}`}`}
                                onClick={(e) => { e.stopPropagation(); togglePip(c, idx); }}>
                                {idx + 1}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div style={S.wrap}>
            <div style={S.head}>
                <div style={S.title}>Class library</div>
                <div style={S.sub}>{classes.length} {classes.length === 1 ? 'class' : 'classes'} · {onDragStart ? 'drag onto a show, or ' : ''}click a show number to place</div>
            </div>
            <div style={S.tools}>
                <div style={S.searchWrap}>
                    <FiSearch size={13} style={S.searchIcon}/>
                    <input style={S.search} placeholder="Search classes" value={search} onChange={(e) => setSearch(e.target.value)}/>
                </div>
                <div style={S.row}>
                    <select style={S.sel} value={fStyle} onChange={(e) => setFStyle(e.target.value)} aria-label="Filter by style">
                        <option value="">Style: All</option>{styleOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <select style={S.sel} value={fTeacher} onChange={(e) => setFTeacher(e.target.value)} aria-label="Filter by teacher">
                        <option value="">Teacher: All</option>{teacherOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div style={S.row}>
                    <select style={S.sel} value={fLevel} onChange={(e) => setFLevel(e.target.value)} aria-label="Filter by level">
                        <option value="">Level: All</option>{levelOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <select style={S.sel} value={groupBy} onChange={(e) => setGroupBy(e.target.value)} aria-label="Group by">
                        <option value="day">Group: Day</option>
                        <option value="placed">Group: Placed / not</option>
                        <option value="style">Group: Style</option>
                        <option value="teacher">Group: Teacher</option>
                        <option value="level">Group: Level</option>
                    </select>
                </div>
            </div>
            <button type="button" style={S.importBtn} onClick={importFromSchedule} disabled={importing}>
                {importing ? 'Importing...' : '⤓ Import classes from schedule'}
            </button>
            <div style={S.importNote}>Imports are copies. Changing the schedule later will not change this recital.</div>
            {unplacedTotal > 0 && (
                <div style={S.warn}>⚠ {unplacedTotal} {unplacedTotal === 1 ? 'class is not' : 'classes are not'} in any show yet</div>
            )}
            <div style={S.scroll}>
                {classes.length === 0 && <p style={S.empty}>No classes yet. Import them from your schedule above, or add a New routine from a show.</p>}
                {classes.length > 0 && filtered.length === 0 && <p style={S.empty}>No classes match.</p>}
                {keys.map((k) => (
                    <React.Fragment key={k}>
                        <div style={S.grp}>{k}<span style={S.grpN}>{grouped[k].length}</span></div>
                        {grouped[k].map(renderRow)}
                    </React.Fragment>
                ))}
                {routines.length > 0 && (
                    <>
                        <div style={S.grp}>New routines<span style={S.grpN}>{routines.length}</span></div>
                        {routines.map(renderRow)}
                    </>
                )}
            </div>
        </div>
    );
}
